import React, { ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import styles from './reviewFilterStyles.module.scss';
import { AppliedReviewFilter, ReviewFilter, ReviewSort } from '../../../types/reviews';
import { Accordion, AccordionDetails, AccordionSummary, useMediaQuery } from '@material-ui/core';
import { MUI_MOBILE_BREAKPOINT } from 'src/scripts/utils/constants';
import {
  getProductReviewFiltersCount,
  getSelectedProductReviewFiltersCount,
  REVIEW_SORT_OPTIONS_ARRAY,
  reviewSidebarBodySelector,
  reviewSidebarHeaderSelector,
} from 'src/scripts/utils/reviews';
import AloSidebar from 'src/scripts/templates/_shared/ui-components/YogaUI/alo-ui/AloSidebar';
import ButtonWithProgress from 'src/scripts/templates/_shared/ui-components/YogaUI/ButtonWithProgress';
import Checkbox from 'src/scripts/templates/_shared/ui-components/YogaUI/Checkbox';
import { StarsUI } from '../../Reviews';
import { ProductReviewContext } from '../ProductReviewContext';
import RadioButtons from 'src/scripts/templates/_shared/ui-components/YogaUI/RadioButtons';
import ReviewAIKeywords from '../ReviewSearchAndKeywords/ReviewAIKeywords';
import AppliedProductReviewFilters from '../AppliedProductReviewFilters';

const ProductReviewFilters = ({
  selectedFilters,
  filters,
  onClearAllProductReviewFilters,
  toggleAppliedProductReviewFilter,
  sortValue,
  setSortValue,
  keywords,
  activeKeyword,
  setActiveKeyword,
  loading,
  productReviewsTotalCount,
}: {
  filters: ReviewFilter[];
  selectedFilters: AppliedReviewFilter[];
  onClearAllProductReviewFilters: () => void;
  toggleAppliedProductReviewFilter: (
    appliedFilters: AppliedReviewFilter[],
    key: string,
    value: string,
    checked: boolean
  ) => void;
  sortValue: ReviewSort;
  setSortValue: (value: ReviewSort) => void;
  keywords: string[] | null;
  activeKeyword: string | undefined;
  setActiveKeyword: React.Dispatch<React.SetStateAction<string | undefined>>;
  loading: boolean;
  productReviewsTotalCount: number;
}) => {
  const {
    isProductReviewFiltersDrawerOpen,
    toggleProductReviewFiltersDrawer,
    setActiveReviewFilterAccordion,
  } = useContext(ProductReviewContext);
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });

  const isFilterValueSelected = useMemo(() => {
    return (filter: ReviewFilter, value: string) => {
      return selectedFilters.some(
        (selectedFilter) =>
          selectedFilter.key === filter.filtertype.label &&
          selectedFilter.values.some((selectedFilterValue) => selectedFilterValue === value)
      );
    };
  }, [selectedFilters]);

  const closeFiltersSidebar = () => {
    setActiveReviewFilterAccordion('');
    toggleProductReviewFiltersDrawer(false);
  };

  const openFiltersSidebar = (id: string) => {
    setActiveReviewFilterAccordion(id);
    toggleProductReviewFiltersDrawer(true);
  };

  const totalFilterCount = getProductReviewFiltersCount(selectedFilters, activeKeyword);

  return (
    <>
      <div className={styles.pillButtons}>
        {!isMobile ? (
          <>
            <button className="button-ghost-xs" onClick={() => openFiltersSidebar('sort')}>
              All Filters{totalFilterCount ? ` (${totalFilterCount})` : ''} <span className="alo-filters-icon"></span>
            </button>
            <ReviewQuickFilterPills
              filters={filters}
              openFiltersSidebar={openFiltersSidebar}
              selectedFilters={selectedFilters}
            />
          </>
        ) : (
          <button className="button-ghost-xs" onClick={() => openFiltersSidebar('')}>
            <span className="alo-filters-icon"></span> All filters{totalFilterCount ? ` (${totalFilterCount})` : ''}
          </button>
        )}
      </div>
      <AloSidebar
        mobileWidth={`100vw`}
        wrapperClass={styles.reviewSidebarWrapper}
        visible={isProductReviewFiltersDrawerOpen}
        onClose={closeFiltersSidebar}
        title={`Filters Selected${totalFilterCount ? ` (${totalFilterCount})` : ' (0)'}`}
        headerBorder={true}
        anchor={'left'}
        desktopWidth={490}
        postHeader={
          <AppliedProductReviewFilters
            filters={filters}
            appliedFilters={selectedFilters}
            toggleAppliedProductReviewFilter={toggleAppliedProductReviewFilter}
            onClearAllProductReviewFilters={onClearAllProductReviewFilters}
            activeKeyword={activeKeyword}
            setActiveKeyword={setActiveKeyword}
          />
        }
      >
        <div className={styles.filtersWrapper}>
          <div className={styles.reviewFilterContent}>
            {isMobile ? <MobileSortOptions sortValue={sortValue} setSortValue={setSortValue} /> : null}
            {filters.map((filter) => {
              const filtersCount = getSelectedProductReviewFiltersCount(filter, selectedFilters);
              const ReviewListFilterLabel = `${filter.filtertype.display_value}${
                filtersCount ? ` (${filtersCount})` : ''
              }`;
              return (
                <ReviewListFilter
                  id={filter.filtertype.label}
                  label={ReviewListFilterLabel}
                  children={
                    <>
                      <div className={styles.reviewCheckboxes}>
                        {filter.filteroptions.map((filteroption) => (
                          <ListFilterCheckbox
                            type={filter.filtertype.label}
                            key={filteroption.key}
                            id={filteroption.key}
                            label={filteroption.display_value}
                            isChecked={isFilterValueSelected(filter, filteroption.key)}
                            onChange={(checked: boolean) => {
                              toggleAppliedProductReviewFilter(
                                selectedFilters,
                                filter.filtertype.label,
                                filteroption.key,
                                checked
                              );
                            }}
                          />
                        ))}
                      </div>
                    </>
                  }
                />
              );
            })}
            {keywords && keywords?.length > 0 ? (
              <ReviewKeywords keywords={keywords} activeKeyword={activeKeyword} setActiveKeyword={setActiveKeyword} />
            ) : null}
          </div>
          <div className={styles.stickyProgressButton}>
            <ButtonWithProgress
              options={{
                onClick: closeFiltersSidebar,
                showProgressAnimation: loading,
                disabled: loading,
                buttonTypeClass: 'button-primary',
                cta: `View Results (${productReviewsTotalCount})`,
              }}
            />
          </div>
        </div>
      </AloSidebar>
    </>
  );
};

const ListFilterCheckbox = ({
  id,
  label,
  type,
  isChecked,
  onChange,
}: {
  id: string;
  label: string;
  type?: string;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
}) => {
  return (
    <div className={styles.reviewFilterFilterCheckbox}>
      <Checkbox
        key={id}
        options={{
          id: id,
          label: type === 'rating' ? <RatingLabel id={id} /> : label,
          name: id,
          value: id,
          checked: isChecked,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.checked);
          },
        }}
      />
    </div>
  );
};

const RatingLabel = ({ id }: { id: string }) => {
  return (
    <label htmlFor={id}>
      <StarsUI score={Number(id)} starSize="17px" color="var(--highlight-medium)" />
    </label>
  );
};

const FilterAccordionBlock = ({ id, children }: { children: ReactNode; id: string }) => {
  const filterItemBlockRef = useRef<HTMLDivElement>(null);
  const { isProductReviewFiltersDrawerOpen, activeReviewFilterAccordion, setActiveReviewFilterAccordion } = useContext(
    ProductReviewContext
  );
  const [expanded, setExpanded] = React.useState(false);
  const filterItemBlock = filterItemBlockRef?.current;

  const handleChange = () => (event: React.ChangeEvent<{}>, expanded: boolean) => {
    setExpanded(expanded);
    activeReviewFilterAccordion === id && setActiveReviewFilterAccordion('');
  };

  useEffect(() => {
    isProductReviewFiltersDrawerOpen ? setExpanded(activeReviewFilterAccordion === id) : setExpanded(false);
  }, [isProductReviewFiltersDrawerOpen, activeReviewFilterAccordion, id]);

  useEffect(() => {
    if (expanded && activeReviewFilterAccordion === id && filterItemBlock) {
      const scroller = document.querySelector(reviewSidebarBodySelector);
      if (scroller) {
        const filterHeader = document.querySelector(reviewSidebarHeaderSelector);
        const { height: filterHeaderHeight = 0 } = filterHeader?.getBoundingClientRect() || {};
        const { top: filterItemBlockTop = 0 } = filterItemBlock?.getBoundingClientRect();
        scroller.scrollTop = filterItemBlockTop - filterHeaderHeight + scroller.scrollTop;
      }
    }
  }, [expanded, activeReviewFilterAccordion, id, filterItemBlock]);

  return children ? (
    <Accordion
      classes={{ root: styles.reviewFilterAccordion, expanded: styles.reviewFilterAccordionExpanded }}
      TransitionProps={activeReviewFilterAccordion === id ? { timeout: 0 } : undefined}
      ref={filterItemBlockRef}
      expanded={expanded}
      onChange={handleChange()}
    >
      {children}
    </Accordion>
  ) : null;
};

const ReviewKeywords = ({
  keywords,
  activeKeyword,
  setActiveKeyword,
}: {
  keywords: string[] | null;
  activeKeyword: string | undefined;
  setActiveKeyword: React.Dispatch<React.SetStateAction<string | undefined>>;
}) => {
  return (
    <>
      <AccordionSummary
        classes={{ root: styles.reviewFilterAccordionSummary, content: styles.reviewFilterAccordionSummaryContent }}
        expandIcon={null}
      >
        Tags{activeKeyword ? ` (1)` : ''}
      </AccordionSummary>
      <AccordionDetails className={styles.reviewFilterAccordionDetails}>
        <ReviewAIKeywords
          keywords={keywords}
          activeKeyword={activeKeyword}
          setActiveKeyword={setActiveKeyword}
          disableShowMore={true}
        />
      </AccordionDetails>
    </>
  );
};

const MobileSortOptions = ({
  sortValue,
  setSortValue,
}: {
  sortValue: ReviewSort;
  setSortValue: (value: ReviewSort) => void;
}) => {
  const options = REVIEW_SORT_OPTIONS_ARRAY.map((sortOption) => [sortOption.label, sortOption.value]);
  const setSortByValue = (sortByValue: string) => {
    setSortValue(sortByValue as ReviewSort);
  };

  return (
    <FilterAccordionBlock id={'sort'}>
      <AccordionSummary
        classes={{ root: styles.reviewFilterAccordionSummary, content: styles.reviewFilterAccordionSummaryContent }}
        expandIcon={<span className={styles.aloChevronDownIcon}></span>}
      >
        Sort By
      </AccordionSummary>
      <AccordionDetails className={styles.reviewFilterAccordionDetails}>
        <RadioButtons
          options={{
            labelValuePairs: options,
            radioRowClasses: styles.reviewFilterRadioButton,
            defaultSelected: true,
            defaultValue: sortValue,
          }}
          setRadioValue={setSortByValue}
        />
      </AccordionDetails>
    </FilterAccordionBlock>
  );
};

const ReviewListFilter = ({ id, label, children }: { id: string; label: string; children: ReactNode }) => {
  return (
    <FilterAccordionBlock id={id}>
      <AccordionSummary
        classes={{ root: styles.reviewFilterAccordionSummary, content: styles.reviewFilterAccordionSummaryContent }}
        expandIcon={<span className={styles.aloChevronDownIcon}></span>}
      >
        {label}
      </AccordionSummary>
      <AccordionDetails className={styles.reviewFilterAccordionDetails}>{children}</AccordionDetails>
    </FilterAccordionBlock>
  );
};

const ReviewQuickFilterPills = ({
  filters,
  openFiltersSidebar,
  selectedFilters,
}: {
  filters: ReviewFilter[];
  openFiltersSidebar: (id: string) => void;
  selectedFilters: AppliedReviewFilter[];
}) => {
  const openDrawer = (id: string) => {
    openFiltersSidebar(id);
  };

  return (
    <>
      {filters.slice(0, 3).map((filter) => {
        if (!filter) return null;
        const filtersCount = getSelectedProductReviewFiltersCount(filter, selectedFilters);
        const ReviewListFilterLabel = `${filter.filtertype.display_value}${filtersCount ? ` (${filtersCount})` : ''}`;
        return (
          <button
            key={filter.filtertype.label}
            className="button-ghost-xs"
            onClick={() => openDrawer(filter.filtertype.label)}
          >
            {ReviewListFilterLabel}
          </button>
        );
      })}
    </>
  );
};

export default ProductReviewFilters;
